/**
 * ADR-0180 slice 3b — account danger zone.
 *
 * Owner-only. Request deletion → the account enters a 30-day grace
 * window (sites go dark, every session is signed out); the owner can
 * cancel any time during grace. At grace expiry the account is
 * permanently deleted (personal data erased; the signed audit chain is
 * retained, scrubbed). Requesting requires typing a fixed confirm
 * phrase, PLUS a single-use code emailed to the owner (3c-3 step-up):
 * a hijacked session alone cannot initiate deletion.
 */

import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '../../app/auth-context.js';
import { useToast } from '../../app/providers.js';
import { useOptionalHttp } from '../../api/http-context.js';
import {
  cancelDeletion,
  getDeletionStatus,
  requestDeletion,
  requestDeletionChallenge,
  type DeletionStatusWire,
} from '../../api/account-deletion.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

const CONFIRM_PHRASE = 'delete my account';

function daysLeft(grace_until: string | null): number | null {
  if (grace_until === null) return null;
  const ms = new Date(grace_until).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

export function DangerPanel(): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const toast = useToast();
  const auth = useAuth();

  const user = auth.state.kind === 'authenticated' ? auth.state.user : null;
  const isOwner = user?.role === 'owner';

  const [status, setStatus] = useState<DeletionStatusWire | null>(null);
  const [loading, setLoading] = useState(true);
  const [typed, setTyped] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async (): Promise<void> => {
    if (http === null) return;
    setLoading(true);
    const res = await getDeletionStatus(http);
    if (res.kind === 'ok') setStatus(res.value);
    setLoading(false);
  }, [http]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onSendCode = async (): Promise<void> => {
    if (http === null) return;
    setBusy(true);
    const res = await requestDeletionChallenge(http);
    setBusy(false);
    if (res.kind === 'ok') {
      setCodeSent(true);
      toast({ tone: 'mint', title: 'Confirmation code sent to your email. It expires in 10 minutes.' });
    } else {
      toast({ tone: 'coral', title: 'Could not send code.', body: res.message });
    }
  };

  const onRequest = async (): Promise<void> => {
    if (http === null || code.trim().length === 0) return;
    setBusy(true);
    const res = await requestDeletion(http, code.trim());
    if (res.kind === 'ok') {
      // The request took every session offline server-side. Sign out
      // cleanly and route to login rather than resetting the panel and
      // doing a background status reload that 401s — the expected
      // behavior is "get signed out, and that's it" (founder report
      // 2026-07-13). The owner signs back in within 30 days to cancel.
      toast({
        tone: 'amber',
        title: 'Your account is scheduled for deletion.',
        body: 'You have been signed out. Sign back in within 30 days to cancel — after that it is permanent.',
      });
      // One-shot flag so the login screen we're about to land on can
      // confirm what happened + how to undo (the toast doesn't survive
      // the navigation) — founder request 2026-07-13.
      try {
        window.sessionStorage.setItem('sill_deletion_requested', '1');
      } catch {
        /* sessionStorage unavailable — the login notice is best-effort */
      }
      await auth.signOut();
      return;
    }
    setBusy(false);
    toast({ tone: 'coral', title: 'Could not request deletion.', body: res.message });
  };

  const onCancel = async (): Promise<void> => {
    if (http === null) return;
    setBusy(true);
    const res = await cancelDeletion(http);
    if (res.kind === 'ok') {
      toast({ tone: 'lime', title: 'Deletion canceled. Bringing your sites back…' });
      // Hard-reload so the restored sites reappear everywhere without a
      // manual refresh (founder request 2026-07-13).
      if (typeof window !== 'undefined') {
        window.setTimeout(() => window.location.reload(), 900);
      }
      return;
    }
    setBusy(false);
    toast({ tone: 'coral', title: 'Could not cancel.', body: res.message });
  };

  if (loading) {
    return (
      <div style={{ fontFamily: sansFont, fontSize: 14, color: C.muted }}>
        Loading…
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div style={{ fontFamily: sansFont, fontSize: 14, color: C.body, maxWidth: '60ch' }}>
        Only the account owner can delete the account.
      </div>
    );
  }

  const pending = status?.pending === true;
  const left = daysLeft(status?.grace_until ?? null);

  return (
    <div style={{ maxWidth: '68ch' }}>
      <div
        style={{
          border: `1px solid ${C.coralFg}`,
          borderRadius: 4,
          padding: 20,
          background: 'transparent',
        }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: C.coralFg,
            marginBottom: 8,
          }}
        >
          Danger zone
        </div>

        {pending ? (
          <>
            <h3
              style={{
                fontFamily: sansFont,
                fontSize: 16,
                fontWeight: 600,
                color: C.text,
                margin: '0 0 8px',
              }}
            >
              Deletion scheduled
            </h3>
            <p style={{ fontFamily: sansFont, fontSize: 14, lineHeight: 1.6, color: C.body, marginBottom: 8 }}>
              This account is scheduled for permanent deletion in{' '}
              <strong>{left ?? 0} day{left === 1 ? '' : 's'}</strong>. Your
              sites are offline and everyone has been signed out. Cancel now to
              restore access — after the window closes, deletion cannot be
              undone.
            </p>
            <button
              type="button"
              onClick={() => void onCancel()}
              disabled={busy}
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                fontWeight: 600,
                padding: '9px 16px',
                marginTop: 6,
                background: C.lime,
                color: C.ink,
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
                opacity: busy ? 0.6 : 1,
              }}
            >
              Cancel deletion &amp; restore
            </button>
          </>
        ) : (
          <>
            <h3
              style={{
                fontFamily: sansFont,
                fontSize: 16,
                fontWeight: 600,
                color: C.text,
                margin: '0 0 8px',
              }}
            >
              Delete this account
            </h3>
            <p style={{ fontFamily: sansFont, fontSize: 14, lineHeight: 1.6, color: C.body, marginBottom: 12 }}>
              Requesting deletion immediately takes your sites offline and signs
              everyone out. You have <strong>30 days</strong> to cancel by
              signing back in. After that, all personal data is permanently
              erased. Your signed, tamper-evident audit records are retained in
              anonymized form for integrity.
            </p>
            {!codeSent ? (
              <button
                type="button"
                onClick={() => void onSendCode()}
                disabled={busy}
                style={{
                  fontFamily: sansFont,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '9px 16px',
                  background: 'transparent',
                  color: C.coralFg,
                  border: `1px solid ${C.coralFg}`,
                  borderRadius: 3,
                  cursor: 'pointer',
                  opacity: busy ? 0.5 : 1,
                }}
              >
                Email me a confirmation code
              </button>
            ) : (
              <>
                <p style={{ fontFamily: sansFont, fontSize: 13, color: C.body, marginBottom: 10 }}>
                  We emailed a code to your address. Enter it and confirm below.
                  {' '}
                  <button
                    type="button"
                    onClick={() => void onSendCode()}
                    disabled={busy}
                    style={{ background: 'none', border: 'none', color: C.limeFg ?? C.body, cursor: 'pointer', padding: 0, fontFamily: sansFont, fontSize: 13, textDecoration: 'underline' }}
                  >
                    Resend
                  </button>
                </p>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="8-character code"
                  autoComplete="one-time-code"
                  style={{
                    fontFamily: monoFont, fontSize: 15, letterSpacing: 2, padding: '9px 12px',
                    width: '100%', maxWidth: 240, background: C.surface2 ?? 'transparent',
                    color: C.text, border: `1px solid ${C.border}`, borderRadius: 3,
                    marginBottom: 12, display: 'block',
                  }}
                />
                <label style={{ display: 'block', fontFamily: monoFont, fontSize: 11, color: C.muted, marginBottom: 6 }}>
                  Type <span style={{ color: C.text }}>{CONFIRM_PHRASE}</span> to confirm
                </label>
                <input
                  type="text"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={CONFIRM_PHRASE}
                  style={{
                    fontFamily: sansFont, fontSize: 14, padding: '9px 12px', width: '100%',
                    maxWidth: 320, background: C.surface2 ?? 'transparent', color: C.text,
                    border: `1px solid ${C.border}`, borderRadius: 3, marginBottom: 12, display: 'block',
                  }}
                />
                <button
                  type="button"
                  onClick={() => void onRequest()}
                  disabled={busy || typed.trim() !== CONFIRM_PHRASE || code.trim().length === 0}
                  style={{
                    fontFamily: sansFont, fontSize: 13, fontWeight: 600, padding: '9px 16px',
                    background: 'transparent', color: C.coralFg, border: `1px solid ${C.coralFg}`,
                    borderRadius: 3, cursor: 'pointer',
                    opacity: busy || typed.trim() !== CONFIRM_PHRASE || code.trim().length === 0 ? 0.5 : 1,
                  }}
                >
                  Confirm account deletion
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
